import type { KhqrConfig } from '@telestore/contracts';
import { err, ok, type Result } from '@telestore/shared';
import { findField, parseTlv, sanitizeEmvText, verifyCrc } from './emv.js';
import { ACCOUNT_SUB, KHQR_TAGS } from './khqr.js';

/**
 * Turning a merchant's own KHQR into shop settings.
 *
 * A merchant knows what their ABA standee looks like. They do not know what a
 * "Bakong account id" is, and there is no reason they should: every field the
 * settings form asks for is already printed in the QR sitting on their counter.
 *
 * This module reads a payload; it never touches an image. The scanning half
 * lives in the admin app, where the canvas is. Keeping the split means the code
 * that decides WHERE MONEY GOES is testable against a plain string — and the
 * string we test it against is real, decoded from a photograph of a working
 * standee (`fixtures/khqr-payloads.ts`).
 *
 * Two rules earned the hard way run through everything below:
 *
 *   - A bad CRC is a refusal, never a "best effort". It means we misread the
 *     code, and a config built from a misread payload pays a stranger.
 *   - Sub-tag 01 is mandatory. A payload carrying only sub-tag 00 is payable
 *     and undeliverable — that is the shape that swallowed 1,100 KHR on
 *     2026-08-21, and it must not survive being imported either.
 */

export type KhqrImportFailureReason =
  /** Decoded something, but it is not an EMV/KHQR payload. */
  | 'not_khqr'
  /** CRC mismatch — we misread the code, not the merchant's fault. */
  | 'bad_checksum'
  /** No tag 29 or tag 30 template at all. */
  | 'no_account_template'
  /** The 1,100-riel shape: names a bank, names no account. */
  | 'no_account_number'
  /** Tag 59 survives no Latin characters, so no bank app could render it. */
  | 'no_latin_name';

export interface KhqrImportFailure {
  reason: KhqrImportFailureReason;
}

export interface KhqrImport {
  config: KhqrConfig;
  /** Which template the merchant's bank used. Mirrored, never overridden. */
  accountTag: '29' | '30';
  /** '11' for a printed standee, '12' for an amount-locked code. */
  pointOfInitiation: string;
  /**
   * Whether this is an ABA account. Decides what we TELL the merchant — the
   * reconciliation ladder's rungs 2 and 3 read ABA's notifications and nobody
   * else's — and never whether we accept them. A Wing shop sells fine on rung 1.
   */
  isAba: boolean;
}

/** Longest payload we will keep on the config for later re-reading. */
const MAX_SOURCE_PAYLOAD = 512;

function isAbaAccount(bakongAccountId: string): boolean {
  const normalized = bakongAccountId.toLowerCase();
  return normalized.includes('@aba') || normalized.includes('abaakhpp');
}

export function khqrConfigFromPayload(payload: string): Result<KhqrImport, KhqrImportFailure> {
  const trimmed = payload.trim();

  /**
   * Checked before anything is parsed. Every field below is only as trustworthy
   * as this one boolean.
   */
  if (!verifyCrc(trimmed)) {
    // Tell the two apart: a URL from a random QR is a different problem for the
    // merchant than a photograph too blurry to read.
    return err({ reason: /^\d{4}/.test(trimmed) ? 'bad_checksum' : 'not_khqr' });
  }

  let fields;
  try {
    fields = parseTlv(trimmed);
  } catch {
    return err({ reason: 'not_khqr' });
  }

  const merchantTemplate = findField(fields, KHQR_TAGS.merchantAccountMerchant);
  const individualTemplate = findField(fields, KHQR_TAGS.merchantAccountIndividual);
  const template = merchantTemplate ?? individualTemplate;
  if (!template) {
    return err({ reason: 'no_account_template' });
  }
  const accountTag: '29' | '30' = merchantTemplate ? '30' : '29';

  let accountFields;
  try {
    accountFields = parseTlv(template);
  } catch {
    return err({ reason: 'not_khqr' });
  }

  const bakongAccountId = findField(accountFields, ACCOUNT_SUB.bakongAccountId);
  if (!bakongAccountId) {
    return err({ reason: 'no_account_template' });
  }

  /**
   * Sub-tag 01 under either template. Note it lands in `account_number` for BOTH
   * tags and `merchant_id` is never written: sub-tag 01 has exactly one meaning
   * — the number the money reaches — and `account_tag` already carries the only
   * other fact. Writing both would recreate the ambiguity this field replaced.
   */
  const accountNumber = findField(accountFields, ACCOUNT_SUB.merchantId);
  if (!accountNumber) {
    return err({ reason: 'no_account_number' });
  }

  const merchantName = sanitizeEmvText(findField(fields, KHQR_TAGS.merchantName) ?? '', 25);
  if (merchantName === '') {
    return err({ reason: 'no_latin_name' });
  }

  const merchantCity = sanitizeEmvText(findField(fields, KHQR_TAGS.merchantCity) ?? '', 15);
  const bankName = findField(accountFields, ACCOUNT_SUB.acquiringBank);

  const config: KhqrConfig = {
    bakong_account_id: bakongAccountId,
    account_number: accountNumber,
    merchant_name: merchantName,
    merchant_city: merchantCity === '' ? 'Phnom Penh' : merchantCity,
    account_tag: accountTag,
    ...(bankName ? { bank_name: bankName } : {}),
    ...(trimmed.length <= MAX_SOURCE_PAYLOAD ? { source_payload: trimmed } : {}),
  };

  return ok({
    config,
    accountTag,
    pointOfInitiation: findField(fields, KHQR_TAGS.pointOfInitiation) ?? '11',
    isAba: isAbaAccount(bakongAccountId),
  });
}
